import { Box, Divider, Typography, useTheme, Paper, Select, MenuItem, FormControl, Button } from "@mui/material";
import { TableHeaderContainer } from "../components/container/TableHeaderContainer";
import { TableTitleContainer } from "../components/container/TableTitleContainer";
import { ArcElement, BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Title, Tooltip } from "chart.js";
import { t } from "i18next";
import { useEffect, useMemo, useRef, useState } from "react";
import { FaLeaf } from "react-icons/fa";
import BarChartWithTabs from "../components/chart/BarChartWithTabs";
import DoughnutChart, { sustainabilityGreens } from "../components/chart/DoughnutChart";
import DonutChart from "../components/chart/DonutChart";
import { formatTo2Decimals } from "../components/grid/columnDefinitions/columnDefinitions";
import SecondaryTabs from "../components/tabs/SecondaryTabs";
import ProcessSummaryTable from "./ProcessSummaryTable";
import { Convert, GetUnitCategory } from "../components/Unit";
import { EnvFactor } from "../db_access/env_factor";
import { getOrgTokens, ORG_PALETTE } from "../theme/style/organizationPalette";
import { GRAPH_PALETTE } from "../theme/style/graphChartPalette";
import "../style/components-style/Footprint.css";

ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export const doughnutOptions = (pageType, totalWeight, totalGWP, totalBiogenicGWP, isDark) => {
  return {
    plugins: {
      annotation: {
        annotations: {
          innerlabel: {
            type: "doughnutLabel",
            display: true,
            drawTime: "afterDraw",
            font: [
              { size: 24, family: "Poppins, sans-serif" },
              { size: 16, family: "Poppins, sans-serif", weight: 500 },
              { size: 16, family: "Poppins, sans-serif", weight: 500 },
            ],
            click(context) {
              console.log("click", context);
            },
            color: isDark ? ["#f1f5f9", "#f1f5f9", "#4ade80"] : ["black", "black", "#649837"],
            content: () => {
              if (pageType == "Material") {
                return [`Weight`, `Total: ${totalWeight > 1 ? totalWeight.toFixed(2) : totalWeight.toPrecision(2)}`];
              } else {
                return [`EC`, `Fossil: ${totalGWP.toFixed(2)}`, `Biogenic: ${totalBiogenicGWP.toFixed(2)}`];
              }
            },
          },
        },
      },

      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        backgroundColor: isDark ? "rgba(255, 255, 255, 0.9)" : "rgba(0, 0, 0, 0.8)",
        titleColor: isDark ? "#000" : "#fff",
        bodyColor: isDark ? "#000" : "#fff",
        bodyFont: {
          family: "'Poppins', sans-serif",
          weight: 500,
          size: 14,
        },
        titleFont: {
          family: "'Poppins', sans-serif",
          weight: 600,
          size: 16,
        },
        padding: 10,
        cornerRadius: 8,
        callbacks: {
          label: (context) => {
            const index = context.dataIndex;
            const dataset = context.dataset;
            const value = dataset.data[index];
            return `${context.label}: ${value !== undefined && value !== null ? Number(value).toFixed(2) : '0.00'}`;
          },
        },
      },
      datalabels: {
        display: false,
      },
    },
    // maintainAspectRatio: true,
    responsive: true,
    interaction: {
      mode: "nearest",
      intersect: true,
    },
  };
};
export const fetchAndProcessData = (processData, setCumulativeGWP, setCumulativeWeight) => {
  const filteredProcesses = processData?.filter((process) => process.lca_stage.startsWith("A1")) || [];
  EnvFactor.get_by_process_ids(filteredProcesses.map((p) => p.id))
    .then((res) => res.json())
    .then((data) => {
      if (data) {
        const tempGWP = {};
        const tempWeight = {};
        data.forEach((ef) => {
          const { description, gwp_total, quantity, factor, unit, density } = ef;
          // Calculate cumulative GWP
          if (tempGWP[description]) {
            tempGWP[description] += gwp_total;
          } else {
            tempGWP[description] = gwp_total;
          }

          // Convert quantity to kilograms
          let weightInKg = 0;
          if (GetUnitCategory(unit) === "weight") {
            weightInKg = Convert(quantity, unit, "kg");
          } else {
            weightInKg = quantity * density;
          }

          // Calculate cumulative Weight in kg
          const weight = weightInKg * factor;
          if (tempWeight[description]) {
            tempWeight[description] += weight;
          } else {
            tempWeight[description] = weight;
          }
        });
        setCumulativeGWP(tempGWP);
        setCumulativeWeight(tempWeight);
      }
    });
};
export const getTotalWeight = (cumulativeWeight) => Object.values(cumulativeWeight).reduce((sum, value) => sum + value, 0);
export const getTotalGWP = (totalGWPs) => Object.values(totalGWPs).reduce((sum, value) => sum + value, 0);
export const getTotalGWPs = (pageType, processData) => {
  let temp = {};
  if (pageType === "supplier") {
    temp = {
      a1: 0,
      a2: 0,
      a3: 0,
    };
    processData.forEach((item) => {
      if (item.lca_stage.startsWith("A1")) temp = { ...temp, a1: temp.a1 + item.gwp };
      if (item.lca_stage.startsWith("A2")) temp = { ...temp, a2: temp.a2 + item.gwp };
      if (item.lca_stage.startsWith("A3")) temp = { ...temp, a3: temp.a3 + item.gwp };
    });
  } else if (pageType === "utilizer") {
    temp = {
      product: 0,
      construction: 0,
      use: 0,
      operational: 0,
      endOfLife: 0,
      benefits: 0,
    };
    processData.forEach((item) => {
      if (["A1", "A2", "A3"].some((prefix) => item.lca_stage.startsWith(prefix))) {
        temp = { ...temp, product: temp.product + item.gwp };
      } else if (["A4", "A5"].some((prefix) => item.lca_stage.startsWith(prefix))) {
        temp = { ...temp, construction: temp.construction + item.gwp };
      } else if (item.lca_stage.startsWith("B6") || item.lca_stage.startsWith("B7")) {
        temp = { ...temp, operational: temp.operational + item.gwp };
      } else if (item.lca_stage.startsWith("B")) {
        temp = { ...temp, use: temp.use + item.gwp };
      } else if (item.lca_stage.startsWith("C")) {
        temp = { ...temp, endOfLife: temp.endOfLife + item.gwp };
      } else if (item.lca_stage.startsWith("D")) {
        temp = { ...temp, benefits: temp.benefits + item.gwp };
      }
    });
  }
  return temp;
};

export const getStageColor = (stageStr) => {
  if (!stageStr) return "#3b82f6";
  const s = stageStr.toUpperCase();
  if (s.startsWith("A1")) return "#ef4444"; // Scope 1 / Stage A1 -> Red
  if (s.startsWith("A2")) return "#f59e0b"; // Stage A2 -> Orange
  if (s.startsWith("A3")) return "#0ea5e9"; // Stage A3 -> Blue
  if (s.startsWith("A") || s === "PRODUCT" || s === "CONSTRUCTION") return "#22c55e"; // A4, A5, etc -> Green
  if (s.startsWith("B") || s.includes("USE") || s === "OPERATIONAL") return "#14b8a6"; // Scope 2 / Stage B -> Teal
  if (s.startsWith("C") || s === "END OF LIFE" || s === "ENDOFLIFE") return "#ec4899"; // Scope 3 / Stage C -> Pink
  if (s.startsWith("D") || s.includes("AVOID") || s === "BENEFITS") return "#8b5cf6"; // Scope 4 / Stage D -> Purple
  if (s.includes("BIOGENIC")) return "#8b5cf6"; // Biogenic -> Purple
  return "#3b82f6"; // Fallback blue
};

const orgPalette = ["#ef4444", "#0ea5e9", "#f59e0b", "#22c55e", "#8b5cf6", "#14b8a6", "#ec4899", "#f97316", "#3b82f6"];

export const getTotalBiogenicGWP = (processData) =>
  processData?.reduce((sum, item) => {
    return sum + (item.biogenic_gwp || 0);
  }, 0) || 0;

export const getFilteredDoughnutData = (doughnutData, doughnutBackgroundColors, doughnutBorderColors) => ({
  labels: doughnutData.labels.filter((_, index) => doughnutData.datasets[0].data[index] > 0),
  datasets: [
    {
      ...doughnutData.datasets[0],
      data: doughnutData.datasets[0].data.filter((value) => value > 0),
      backgroundColor: doughnutData.datasets[0].data.map((value, index) => (value > 0 ? doughnutBackgroundColors[index] : null)).filter((color) => color !== null),
      borderColor: doughnutData.datasets[0].data.map((value, index) => (value > 0 ? doughnutBorderColors[index] : null)).filter((color) => color !== null),
    },
  ],
});
export const getDoughnutData = (doughnutLabels, cumulativeWeightDataPoints, pageType, totalGWPs, doughnutBackgroundColors, doughnutBorderColors) => ({
  labels: doughnutLabels,
  datasets: [
    {
      data:
        pageType == "Material"
          ? cumulativeWeightDataPoints
          : pageType === "supplier"
            ? [totalGWPs.a1, totalGWPs.a2, totalGWPs.a3].map((value) => parseFloat(value?.toFixed(2) || 0))
            : [totalGWPs.product, totalGWPs.construction, totalGWPs.use, totalGWPs.operational, totalGWPs.endOfLife, totalGWPs.benefits].map((value) =>
              parseFloat(value?.toFixed(2) || 0)
            ),
      backgroundColor: doughnutBackgroundColors,
      borderColor: doughnutBorderColors,
      borderWidth: 1,
    },
  ],
});
export const getColors = (doughnutLabels, sustainabilityGreens) => {
  const temp = [];
  doughnutLabels.forEach((label, index) => {
    const color = sustainabilityGreens[index % sustainabilityGreens.length];
    temp.push(color);
  });
  return temp;
};
export const getDoughnutLabels = (pageType, cumulativeWeightLabels) => {
  return pageType === "Material"
    ? cumulativeWeightLabels
    : pageType === "supplier"
      ? ["A1", "A2", "A3"]
      : ["Product", "Construction", "Use", "Operational", "End-of-life", "Benefits"];
};
function Footprint({ processData, pageType, hideCharts = false, hideTable = false, hidePagination = false }) {
  const [selectedLCAStage, setSelectedLCAStage] = useState("All");
  const [cumulativeGWP, setCumulativeGWP] = useState({});
  const [cumulativeWeight, setCumulativeWeight] = useState({});
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);
  const [LCAStages, setLCAStages] = useState([]);
  const [activeTab, setActiveTab] = useState("Environmental");
  const [showAllDonutLegend, setShowAllDonutLegend] = useState(false);
  const ref = useRef(null);

  // Create a filtered dataset excluding 0 values for the doughnut chart
  const [filteredDoughnutData, setfilteredDoughnutData] = useState();
  const [cumulativeGWPLabels, setcumulativeGWPLabels] = useState([]);
  const [cumulativeGWPDataPoints, setcumulativeGWPDataPoints] = useState([]);
  const [cumulativeWeightLabels, setcumulativeWeightLabels] = useState([]);
  const [cumulativeWeightDataPoints, setcumulativeWeightDataPoints] = useState([]);
  const [cumulativeGWPBackgroundColors, setcumulativeGWPBackgroundColors] = useState([]);
  const [cumulativeGWPBorderColors, setcumulativeGWPBorderColors] = useState([]);
  const [cumulativeWeightBackgroundColors, setcumulativeWeightBackgroundColors] = useState([]);
  const [cumulativeWeightBorderColors, setcumulativeWeightBorderColors] = useState([]);
  const [barLabels, setbarLabels] = useState([]);
  const [barDataPoints, setbarDataPoints] = useState([]);
  const [backgroundColors, setbackgroundColors] = useState([]);
  const [borderColors, setborderColors] = useState([]);
  const [adjustedData, setadjustedData] = useState({
    labels: [],
    data: [],
    backgroundColors: [],
    borderColors: [],
  });
  const [barData, setbarData] = useState();
  const [totalGWPs, settotalGWPs] = useState();
  const [totalGWP, settotalGWP] = useState(0);
  const [doughnutBackgroundColors, setdoughnutBackgroundColors] = useState([]);
  const [doughnutBorderColors, setdoughnutBorderColors] = useState([]);
  const [doughnutLabels, setdoughnutLabels] = useState([]);
  const [doughnutData, setdoughnutData] = useState();
  // Adjust the dataset count only for cumulative GWP data
  const [adjustedCumulativeGWPData, setadjustedCumulativeGWPData] = useState();
  // Create the cumulative GWP data object with adjusted data
  const [cumulativeGWPData, setcumulativeGWPData] = useState();
  const [cumulativeWeightData, setcumulativeWeightData] = useState();
  const [totalWeight, setTotalWeight] = useState(0);
  const [filteredProcessData, setfilteredProcessData] = useState();
  const [totalBiogenicGWP, settotalBiogenicGWP] = useState(0);
  const supplierLCAStages = [
    { label: "All", value: "All" },
    { label: "A1: Raw Material", value: "A1" },
    { label: "A2: Transport", value: "A2" },
    { label: "A3: Manufacturing", value: "A3" },
  ];

  const utilizerLCAStages = [
    { label: "All", value: "All" },
    { label: "Product (A1-A3)", value: "A123" },
    { label: "Construction (A4-A5)", value: "A45" },
    { label: "Use (B1-B5)", value: "B12345" },
    { label: "Operational (B6-B7)", value: "B67" },
    { label: "End-of-life (C)", value: "C" },
    { label: "Benefits (D)", value: "D" },
  ];

  const tabs = [
    { label: "Environmental", value: "Environmental" },
    { label: "Material", value: "Material" },
  ];

  const barChartFilename = "environmental_footprint.png";
  const pieChartFilename = "footprint_overview.png";
  const adjustDatasetCount = (labels, data, backgroundColors, borderColors) => {
    const minBars = 4;
    const currentCount = labels.length;
    const newLabels = [...labels];
    const newData = [...data];
    const newBg = [...backgroundColors];
    const newBc = [...borderColors];

    if (currentCount < minBars) {
      const missingBars = minBars - currentCount;

      for (let i = 0; i < missingBars; i++) {
        newLabels.push("");
        newData.push(0);
        newBg.push("transparent");
        newBc.push("transparent");
      }
    }

    return { labels: newLabels, data: newData, backgroundColors: newBg, borderColors: newBc };
  };
  const adjustGWPDatasetCount = (labels, data, backgroundColors, borderColors) => {
    const minBars = 4;
    const currentCount = labels.length;
    const newLabels = [...labels];
    const newData = [...data];
    const newBg = [...backgroundColors];
    const newBc = [...borderColors];

    if (currentCount < minBars) {
      const missingBars = minBars - currentCount;

      for (let i = 0; i < missingBars; i++) {
        newLabels.push("");
        newData.push(0);
        newBg.push("transparent");
        newBc.push("transparent");
      }
    }

    return { labels: newLabels, data: newData, backgroundColors: newBg, borderColors: newBc };
  };

  const carbonImpactContent = useMemo(
    () => [
      {
        icon: FaLeaf,
        title: "Total Biogenic CO₂",
        value: formatTo2Decimals(processData?.reduce((sum, p) => sum + (p.biogenic_gwp || 0), 0) || 0),
        titleColor: "seaGreen",
        valueColor: "black",
      },
      {
        icon: FaLeaf,
        title: "Total Fossil CO₂",
        value: formatTo2Decimals(processData?.reduce((sum, p) => sum + (p.gwp || 0), 0) || 0),
        titleColor: isDark ? "#f1f5f9" : "black",
        valueColor: isDark ? "#f1f5f9" : "black",
      },
      {
        icon: FaLeaf,
        title: "Net Carbon Impact CO₂",
        value: formatTo2Decimals(processData?.reduce((sum, p) => sum + (p.biogenic_gwp || 0) + (p.gwp || 0), 0) || 0),
        titleColor: isDark ? "#4ade80" : "#659d33",
        valueColor: isDark ? "#4ade80" : "#659d33",
      },
    ],
    [processData]
  );
  useEffect(() => {
    if (pageType == "supplier") {
      setLCAStages(supplierLCAStages);
    } else {
      setLCAStages(utilizerLCAStages);
    }
  }, [pageType]);
  useEffect(() => {
    setcumulativeGWPLabels(Object.keys(cumulativeGWP));
    setcumulativeGWPDataPoints(Object.values(cumulativeGWP).map((value) => parseFloat((value || 0).toFixed(2))));
  }, [cumulativeGWP]);

  useEffect(() => {
    setcumulativeWeightLabels(Object.keys(cumulativeWeight));
    setcumulativeWeightDataPoints(Object.values(cumulativeWeight).map((value) => parseFloat((value || 0).toFixed(2))));
  }, [cumulativeWeight]);

  useEffect(() => { }, [cumulativeWeightDataPoints]);

  useEffect(() => {
    const temp = [];
    cumulativeGWPLabels.forEach((label, index) => {
      temp.push(orgPalette[index % orgPalette.length]);
    });
    setcumulativeGWPBackgroundColors(temp);
    setcumulativeGWPBorderColors(temp);
  }, [cumulativeGWPLabels]);

  useEffect(() => {
    const temp = [];
    cumulativeWeightLabels.forEach((label, index) => {
      temp.push(orgPalette[index % orgPalette.length]);
    });
    setcumulativeWeightBackgroundColors(temp);
    setcumulativeWeightBorderColors(temp);
  }, [cumulativeWeightLabels]);

  useEffect(() => {
    const temp =
      selectedLCAStage === "All"
        ? processData
        : selectedLCAStage === "A123"
          ? processData.filter((item) => ["A1", "A2", "A3"].some((prefix) => item.lca_stage.startsWith(prefix)))
          : selectedLCAStage === "A45"
            ? processData.filter((item) => ["A4", "A5"].some((prefix) => item.lca_stage.startsWith(prefix)))
            : selectedLCAStage === "B12345"
              ? processData.filter((item) => ["B1", "B2", "B3", "B4", "B5"].some((prefix) => item.lca_stage.startsWith(prefix)))
              : selectedLCAStage === "B67"
                ? processData.filter((item) => ["B6", "B7"].some((prefix) => item.lca_stage.startsWith(prefix)))
                : processData.filter((item) => item.lca_stage.startsWith(selectedLCAStage));
    setfilteredProcessData(temp);
  }, [processData, selectedLCAStage]);

  useEffect(() => {
    if (filteredProcessData) {
      setbarLabels(filteredProcessData.map((item) => item.name));
      setbarDataPoints(filteredProcessData.map((item) => parseFloat((item?.gwp || 0).toFixed(2))));

      const tempColors = filteredProcessData.map((item) => getStageColor(item.lca_stage));
      setbackgroundColors(tempColors);
      setborderColors(tempColors);
    }
  }, [filteredProcessData]);

  useEffect(() => {
    setadjustedData(adjustDatasetCount(barLabels, barDataPoints, backgroundColors, borderColors));
  }, [barLabels, barDataPoints, backgroundColors, borderColors]);

  useEffect(() => {
    setbarData({
      labels: adjustedData.labels,
      datasets: [
        {
          label: "GWP",
          data: adjustedData.data,
          backgroundColor: adjustedData.backgroundColors,
          borderColor: adjustedData.borderColors,
          borderWidth: 1.5,
          pointRadius: 3,
          pointHoverRadius: 3,
          pointBackgroundColor: adjustedData.backgroundColors,
          tension: 0.05,
          borderRadius: {
            topLeft: 10,
            topRight: 10,
            bottomLeft: 0,
            bottomRight: 0,
          },
        },
      ],
    });
  }, [adjustedData]);

  useEffect(() => {
    settotalGWPs(getTotalGWPs(pageType, processData));
  }, [pageType, processData]);

  useEffect(() => {
    if (totalGWPs) {
      settotalGWP(getTotalGWP(totalGWPs));
    }
  }, [totalGWPs]);

  useEffect(() => {
    setdoughnutLabels(getDoughnutLabels(pageType, cumulativeWeightLabels));
  }, [pageType, cumulativeWeightLabels]);

  useEffect(() => {
    if (totalGWPs) {
      setdoughnutData(getDoughnutData(doughnutLabels, cumulativeWeightDataPoints, pageType, totalGWPs, doughnutBackgroundColors, doughnutBorderColors));
    }
  }, [cumulativeWeightDataPoints, totalGWPs, doughnutBackgroundColors, doughnutBorderColors, doughnutLabels, pageType]);

  useEffect(() => {
    const temp = doughnutLabels.map((label) => getStageColor(label));
    setdoughnutBackgroundColors(temp);
    setdoughnutBorderColors(temp);
  }, [doughnutLabels]);

  useEffect(() => {
    if (doughnutData) {
      setfilteredDoughnutData(getFilteredDoughnutData(doughnutData, doughnutBackgroundColors, doughnutBorderColors));
    }
  }, [doughnutData]);

  useEffect(() => {
    settotalBiogenicGWP(getTotalBiogenicGWP(processData));
  }, [processData]);

  useEffect(() => {
    fetchAndProcessData(processData, setCumulativeGWP, setCumulativeWeight);
  }, [processData]);

  useEffect(() => {
    if (cumulativeGWPLabels && cumulativeGWPDataPoints && cumulativeGWPBackgroundColors && cumulativeGWPBorderColors)
      setadjustedCumulativeGWPData(adjustGWPDatasetCount(cumulativeGWPLabels, cumulativeGWPDataPoints, cumulativeGWPBackgroundColors, cumulativeGWPBorderColors));
  }, [cumulativeGWPLabels, cumulativeGWPDataPoints, cumulativeGWPBackgroundColors, cumulativeGWPBorderColors]);

  useEffect(() => {
    if (adjustedCumulativeGWPData)
      setcumulativeGWPData({
        labels: adjustedCumulativeGWPData.labels,
        datasets: [
          {
            label: "Cumulative GWP",
            data: adjustedCumulativeGWPData.data,
            backgroundColor: adjustedCumulativeGWPData.backgroundColors,
            borderColor: adjustedCumulativeGWPData.borderColors,
            borderWidth: 1,
            pointRadius: 3,
            pointHoverRadius: 3,
            pointBackgroundColor: adjustedCumulativeGWPData.backgroundColors,
            tension: 0.05,
            borderRadius: {
              topLeft: 10,
              topRight: 10,
              bottomLeft: 0,
              bottomRight: 0,
            },
          },
        ],
      });
  }, [adjustedCumulativeGWPData]);

  useEffect(() => {
    setcumulativeWeightData({
      labels: cumulativeWeightLabels,
      datasets: [
        {
          data: cumulativeWeightDataPoints,
          backgroundColor: cumulativeWeightBackgroundColors,
          borderColor: cumulativeWeightBorderColors,
          borderWidth: 1,
        },
      ],
    });
  }, [cumulativeWeightLabels, cumulativeWeightDataPoints, cumulativeWeightBackgroundColors, cumulativeWeightBorderColors]);

  useEffect(() => {
    setTotalWeight(getTotalWeight(cumulativeWeight));
  }, [cumulativeWeight]);

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 4 }}>
      {!hideCharts && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 4, alignItems: "stretch" }} ref={ref}>
          {/* Donut Chart */}
          <Box sx={{ flex: "1 1 25%", minWidth: 350, order: { xs: 2, md: 1 }, display: "flex", flexDirection: "column" }}>
            <Box sx={{ p: ORG_PALETTE.spacing.chartPad, borderRadius: ORG_PALETTE.radius.card, bgcolor: tok.bg.card, border: `1px solid ${tok.border.card}`, display: "flex", flexDirection: "column", gap: 4, height: "100%" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, mb: 4, borderBottom: `1px solid ${tok.border.divider}`, pb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 500, color: tok.text.chartTitle }}>{t("Footprint Summary")}</Typography>
                {pageType == "supplier" && (
                  <FormControl variant="standard">
                    <Select
                      value={activeTab}
                      onChange={(e) => setActiveTab(e.target.value)}
                      disableUnderline
                      sx={{
                        fontSize: "14px", fontWeight: 600, color: "#64748b",
                        "& .MuiSelect-select": { pb: "4px", borderBottom: "2px solid #659d33", paddingRight: "24px !important" },
                        "& .MuiSvgIcon-root": { color: "#659d33" }
                      }}
                    >
                      {tabs.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>{t(opt.label)}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Box>
              {pageType == "supplier" && activeTab == "Material" && (
                <Box>
                  <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
                    <DonutChart
                      data={cumulativeWeightData?.labels?.map((l, i) => ({ label: l, value: cumulativeWeightData.datasets[0].data[i], color: cumulativeWeightData.datasets[0].backgroundColor[i] })).filter(d => d.value > 0) || []}
                      size={240}
                      strokeWidth={24}
                      centerContent={(
                        (() => {
                          const weightStr = totalWeight.toLocaleString(undefined, { maximumFractionDigits: 0 });
                          const dynamicFontSize = weightStr.length <= 6 ? "36px" : weightStr.length <= 9 ? "28px" : weightStr.length <= 12 ? "22px" : "18px";
                          return (
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography sx={{ fontFamily: "Poppins, sans-serif", fontSize: "16px", color: tok.text.muted, display: 'block', fontWeight: 500 }}>TOTAL</Typography>
                              <Typography sx={{ fontFamily: "Poppins, sans-serif", fontSize: dynamicFontSize, fontWeight: 700, color: tok.text.primary, lineHeight: 1.2 }}>{weightStr}</Typography>
                              <Typography sx={{ fontFamily: "Poppins, sans-serif", fontSize: "16px", color: tok.text.muted, fontWeight: 500, userSelect: 'none' }}>kg</Typography>
                            </Box>
                          );
                        })()
                      )}
                    />
                  </Box>
                  {(() => {
                    const legendItems = (cumulativeWeightData?.labels?.map((l, i) => ({
                      label: l,
                      value: cumulativeWeightData.datasets[0].data[i],
                      color: cumulativeWeightData.datasets[0].backgroundColor[i]
                    })).filter(d => d.value > 0).sort((a, b) => b.value - a.value) || []);

                    const displayItems = showAllDonutLegend ? legendItems : legendItems.slice(0, 15);

                    return (
                      <Box sx={{ mt: 2 }}>
                        {legendItems.length > 15 && (
                          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
                            <Button
                              size="small"
                              onClick={() => setShowAllDonutLegend(!showAllDonutLegend)}
                              variant="contained"
                              sx={{
                                textTransform: "none",
                                fontSize: "11px",
                                py: 0.5,
                                px: 1.2,
                                borderRadius: "6px",
                                backgroundColor: "#659d33",
                                color: "#ffffff",
                                border: "1px solid #659d33",
                                "&:hover": {
                                  backgroundColor: "#55842b",
                                  borderColor: "#55842b"
                                }
                              }}
                            >
                              {showAllDonutLegend ? t("Show Top 15") : t("Show All")}
                            </Button>
                          </Box>
                        )}
                        {displayItems.map((d, i) => (
                          <Box key={i} sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, mb: 1.5 }}>
                            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, flex: 1, minWidth: 0 }}>
                              <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: d.color, flexShrink: 0, mt: "3px" }} />
                              <Typography sx={{ fontFamily: GRAPH_PALETTE.font.family, fontSize: "14px", fontWeight: GRAPH_PALETTE.font.weights.standard, color: tok.text.primary, textAlign: "left" }}>{d.label}</Typography>
                            </Box>
                            <Typography sx={{ fontFamily: GRAPH_PALETTE.font.family, fontSize: "14px", fontWeight: GRAPH_PALETTE.font.weights.standard, color: tok.text.primary, flexShrink: 0, textAlign: "right" }}>
                              {d.value.toLocaleString(undefined, { maximumFractionDigits: 0 })} kg ({totalWeight > 0 ? ((d.value / totalWeight) * 100).toFixed(1) : 0}%)
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    );
                  })()}
                </Box>
              )}
              {pageType == "supplier" && activeTab == "Environmental" && (
                <Box>
                  <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
                    <DonutChart
                      data={filteredDoughnutData?.labels?.map((l, i) => ({ label: l, value: filteredDoughnutData.datasets[0].data[i], color: filteredDoughnutData.datasets[0].backgroundColor[i] })).filter(d => d.value > 0) || []}
                      size={240}
                      strokeWidth={24}
                      centerContent={(
                        (() => {
                          const gwpStr = totalGWP.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
                          const dynamicFontSize = gwpStr.length <= 6 ? "36px" : gwpStr.length <= 9 ? "28px" : gwpStr.length <= 12 ? "22px" : "18px";
                          return (
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography sx={{ fontFamily: "Poppins, sans-serif", fontSize: "16px", color: tok.text.muted, display: 'block', fontWeight: 500 }}>TOTAL</Typography>
                              <Typography sx={{ fontFamily: "Poppins, sans-serif", fontSize: dynamicFontSize, fontWeight: 700, color: tok.text.primary, lineHeight: 1.2 }}>{gwpStr}</Typography>
                              <Typography sx={{ fontFamily: "Poppins, sans-serif", fontSize: "16px", color: tok.text.muted, fontWeight: 500, userSelect: 'none' }}>kg CO₂e</Typography>
                            </Box>
                          );
                        })()
                      )}
                    />
                  </Box>
                  <Box sx={{ mt: 2 }}>
                    {(filteredDoughnutData?.labels?.map((l, i) => ({ label: l, value: filteredDoughnutData.datasets[0].data[i], color: filteredDoughnutData.datasets[0].backgroundColor[i] })).filter(d => d.value > 0).sort((a, b) => b.value - a.value) || []).map((d, i) => (
                      <Box key={i} sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, mb: 1.5 }}>
                        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, flex: 1, minWidth: 0 }}>
                          <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: d.color, flexShrink: 0, mt: "3px" }} />
                          <Typography sx={{ fontFamily: GRAPH_PALETTE.font.family, fontSize: "14px", fontWeight: GRAPH_PALETTE.font.weights.standard, color: tok.text.primary, textAlign: "left" }}>{d.label}</Typography>
                        </Box>
                        <Typography sx={{ fontFamily: GRAPH_PALETTE.font.family, fontSize: "14px", fontWeight: GRAPH_PALETTE.font.weights.standard, color: tok.text.primary, flexShrink: 0, textAlign: "right" }}>
                          {d.value.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} ({totalGWP > 0 ? ((d.value / totalGWP) * 100).toFixed(1) : 0}%)
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
              {pageType == "utilizer" && (
                <Box>
                  <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
                    <DonutChart
                      data={filteredDoughnutData?.labels?.map((l, i) => ({ label: l, value: filteredDoughnutData.datasets[0].data[i], color: filteredDoughnutData.datasets[0].backgroundColor[i] })).filter(d => d.value > 0).sort((a, b) => b.value - a.value) || []}
                      size={240}
                      strokeWidth={24}
                      centerContent={(
                        (() => {
                          const gwpStr = totalGWP.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
                          const dynamicFontSize = gwpStr.length <= 6 ? "36px" : gwpStr.length <= 9 ? "28px" : gwpStr.length <= 12 ? "22px" : "18px";
                          return (
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography sx={{ fontFamily: "Poppins, sans-serif", fontSize: "16px", color: tok.text.muted, display: 'block', fontWeight: 500 }}>TOTAL</Typography>
                              <Typography sx={{ fontFamily: "Poppins, sans-serif", fontSize: dynamicFontSize, fontWeight: 700, color: tok.text.primary, lineHeight: 1.2 }}>{gwpStr}</Typography>
                              <Typography sx={{ fontFamily: "Poppins, sans-serif", fontSize: "16px", color: tok.text.muted, fontWeight: 500, userSelect: 'none' }}>kg CO₂e</Typography>
                            </Box>
                          );
                        })()
                      )}
                    />
                  </Box>
                  <Box sx={{ mt: 2 }}>
                    {(filteredDoughnutData?.labels?.map((l, i) => ({ label: l, value: filteredDoughnutData.datasets[0].data[i], color: filteredDoughnutData.datasets[0].backgroundColor[i] })).filter(d => d.value > 0).sort((a, b) => b.value - a.value) || []).map((d, i) => (
                      <Box key={i} sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, mb: 1.5 }}>
                        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, flex: 1, minWidth: 0 }}>
                          <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: d.color, flexShrink: 0, mt: "3px" }} />
                          <Typography sx={{ fontFamily: GRAPH_PALETTE.font.family, fontSize: "14px", fontWeight: GRAPH_PALETTE.font.weights.standard, color: tok.text.primary, textAlign: "left" }}>{d.label}</Typography>
                        </Box>
                        <Typography sx={{ fontFamily: GRAPH_PALETTE.font.family, fontSize: "14px", fontWeight: GRAPH_PALETTE.font.weights.standard, color: tok.text.primary, flexShrink: 0, textAlign: "right" }}>
                          {d.value.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} ({totalGWP > 0 ? ((d.value / totalGWP) * 100).toFixed(1) : 0}%)
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  {/* Biogenic line at the bottom */}
                  {Math.abs(totalBiogenicGWP) > 0 && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${tok.border.divider}`, display: "flex", justifyContent: "space-between", mb: 1.5 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: "#8b5cf6" }} />
                        <Typography sx={{ fontFamily: GRAPH_PALETTE.font.family, fontSize: "14px", fontWeight: GRAPH_PALETTE.font.weights.standard, color: tok.text.primary }}>Biogenic Carbon</Typography>
                      </Box>
                      <Typography sx={{ fontFamily: GRAPH_PALETTE.font.family, fontSize: "14px", fontWeight: GRAPH_PALETTE.font.weights.standard, color: tok.text.primary }}>{totalBiogenicGWP.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}</Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Box>

          {/* Bar Chart Section */}
          <Box sx={{ flex: "1 1 60%", minWidth: 450, order: { xs: 1, md: 1 }, display: "flex", flexDirection: "column" }}>
            <Box sx={{ p: ORG_PALETTE.spacing.chartPad, borderRadius: ORG_PALETTE.radius.card, bgcolor: tok.bg.card, border: `1px solid ${tok.border.card}`, display: "flex", flexDirection: "column", overflowX: "auto", height: "100%" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, mb: 4, borderBottom: `1px solid ${tok.border.divider}`, pb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 500, color: tok.text.chartTitle }}>{t("Emissions Breakdown")}</Typography>

                {pageType == "supplier" && activeTab == "Environmental" && (
                  <FormControl variant="standard">
                    <Select
                      value={selectedLCAStage}
                      onChange={(e) => setSelectedLCAStage(e.target.value)}
                      disableUnderline
                      sx={{
                        fontSize: "14px", fontWeight: 600, color: "#64748b",
                        "& .MuiSelect-select": { pb: "4px", borderBottom: "2px solid #659d33", paddingRight: "24px !important" },
                        "& .MuiSvgIcon-root": { color: "#659d33" }
                      }}
                    >
                      {supplierLCAStages.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>{t(opt.label)}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {pageType == "utilizer" && (
                  <FormControl variant="standard">
                    <Select
                      value={selectedLCAStage}
                      onChange={(e) => setSelectedLCAStage(e.target.value)}
                      disableUnderline
                      sx={{
                        fontSize: "14px", fontWeight: 600, color: "#64748b",
                        "& .MuiSelect-select": { pb: "4px", borderBottom: "2px solid #659d33", paddingRight: "24px !important" },
                        "& .MuiSvgIcon-root": { color: "#659d33" }
                      }}
                    >
                      {LCAStages.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>{t(opt.label)}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Box>

              <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
                {pageType == "supplier" && activeTab == "Material" ? (
                  <BarChartWithTabs key="material" filename={barChartFilename} selected={selectedLCAStage} setSelected={setSelectedLCAStage} data={cumulativeGWPData} sx={{ width: "100%", flex: 1 }} />
                ) : pageType == "supplier" && activeTab == "Environmental" ? (
                  <BarChartWithTabs key="environmental" filename={barChartFilename} selected={selectedLCAStage} setSelected={setSelectedLCAStage} data={barData} sx={{ width: "100%", flex: 1 }} />
                ) : (
                  <BarChartWithTabs filename={barChartFilename} selected={selectedLCAStage} setSelected={setSelectedLCAStage} data={barData} sx={{ width: "100%", flex: 1 }} />
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {/* Detailed Process Summary Table */}
      {!hideTable && (
        <Box sx={{ animation: "org-fadeInUp 0.5s ease" }}>
          <Paper variant="Table Container" sx={{ overflow: "hidden", border: "1px solid", borderColor: tok.border.card, borderRadius: ORG_PALETTE.radius.card, bgcolor: tok.bg.card }}>
            <TableHeaderContainer>
              <TableTitleContainer>
                <Typography variant="Table Title">
                  {pageType == "Material" ? t("Detailed Material Summary") : t("Detailed Process Summary")}
                </Typography>
              </TableTitleContainer>
            </TableHeaderContainer>
            <ProcessSummaryTable
              processData={filteredProcessData || []}
              grossTotal={totalGWP}
              isDark={isDark}
              hidePagination={hidePagination}
            />
          </Paper>
        </Box>
      )}
    </Box>
  );
}

export default Footprint;
